import { toRaw } from 'vue';
import { cloneSubgraph, createGraph, isObjectEndpoint, mergeSubgraph } from './graphModel.js';

/*
 * Copying the node graph that belongs to UI objects, for every graph domain
 * (Logic, Home Assistant). A domain supplies:
 *   graphKey                     page field holding the graph
 *   extract(graph, objectUuids)  the subgraph that belongs to those objects
 *   idPrefix                     node id prefix per kind (cloneSubgraph)
 *   serialize(graph, haspidOf) / deserialize(file, uuidOfHaspid)  the file form
 */

// Copies are placed next to the original, like the copied objects themselves.
export const GRAPH_COPY_OFFSET = Object.freeze({ x: 20, y: 20 });

/**
 * Duplicate the graph of copied objects onto the copies' page. `uuidMap`
 * maps source object uuid → copy uuid. `getPage(node)` is getObjectPage,
 * injected because importing utils/hasputils here would load HaspObject →
 * lookupTables → HaspPage out of order. Returns unpushed history commands
 * (one per target page) for batching with the object creation.
 */
export function copyObjectGraph(layer, uuidMap, store, domain, { getPage, offset = GRAPH_COPY_OFFSET, description }) {
    const groups = new Map();
    const rawLayer = toRaw(layer);
    for (const [sourceUuid, copyUuid] of uuidMap ?? []) {
        const sourcePage = toRaw(getPage(rawLayer.findOne('#' + sourceUuid)));
        const targetPage = toRaw(getPage(rawLayer.findOne('#' + copyUuid)));
        if (!sourcePage?.[domain.graphKey] || !targetPage) continue;
        const key = `${sourcePage.id()}>${targetPage.id()}`;
        if (!groups.has(key)) groups.set(key, { sourcePage, targetPage, uuids: [] });
        groups.get(key).uuids.push(sourceUuid);
    }
    const commands = [];
    for (const { sourcePage, targetPage, uuids } of groups.values()) {
        const subgraph = domain.extract(sourcePage[domain.graphKey], uuids);
        if (subgraph.nodes.length === 0) continue;
        const copy = cloneSubgraph(subgraph, { objectMap: uuidMap, offset, idPrefix: domain.idPrefix });
        const command = store.record(targetPage, mergeSubgraph(targetPage[domain.graphKey], copy), description);
        if (command) commands.push(command);
    }
    return commands;
}

/**
 * File-form graph of an object subtree for a custom component. Object
 * references use the OpenHASP ids of the component's object records.
 */
export function exportObjectTreeGraph(root, getPage, domain) {
    root = toRaw(root);
    const page = toRaw(getPage(root));
    if (!page?.[domain.graphKey]) return undefined;
    const objects = [root, ...(root.find?.(node => typeof node.obj === 'string') ?? [])];
    const haspidOf = new Map(objects.map(node => [node.id(), node.haspid]));
    const subgraph = domain.extract(page[domain.graphKey], [...haspidOf.keys()]);
    if (subgraph.nodes.length === 0) return undefined;
    return domain.serialize({ ...createGraph(), ...subgraph }, uuid => haspidOf.get(uuid));
}

/**
 * Add a custom component's file-form graph to `page`, after its objects were
 * placed. `uuidOfComponentId` maps a component object id to the placed uuid.
 * Not recorded in history, like the component placement itself.
 */
export function placeComponentGraph(page, file, uuidOfComponentId, offset, domain) {
    page = toRaw(page);
    if (!page || !file) return [];
    const { graph, diagnostics } = domain.deserialize(file, uuidOfComponentId);
    const objectUuids = graph.wires.flatMap(wire => [wire.from, wire.to]).filter(isObjectEndpoint).map(end => end.object);
    const identity = new Map(objectUuids.map(uuid => [uuid, uuid]));
    page[domain.graphKey] = mergeSubgraph(page[domain.graphKey],
        cloneSubgraph(graph, { objectMap: identity, offset, idPrefix: domain.idPrefix }));
    return diagnostics;
}
